import { ServiceBase } from './ServiceBase';
import { FETCH_TYPES } from 'util/constants';
import { encodeQuery } from 'util/helpers';
import request from 'util/request';

/*
 * Back-office client for /api/lms (hris-v3 LmsBundle). Base endpoint: REST_API.lms_courses = 'lms/courses'.
 * Inherited `find(query)` is the paginated course list. Every write of the structure returns either the full
 * course (CourseDetails) or a quiz (Quiz); callers re-render from that response and never patch local state.
 *
 * LMS business errors are 400 with errors.children.<key>; util/request re-throws them to the caller.
 * Structure keys from publish / live-edit validation: course, content_<id>, quiz_<id>, question_<id>.
 */

/**
 * @typedef {{ items: Array<T>, total: number, count: number,
 *   pagination: { self: number, first: number, last: number, next?: number, prev?: number } }} Paginated
 * @template T
 */

/** @typedef {{ id: number, name: string }} Category */

/** @typedef {'draft'|'published'|'unpublished'} CourseStatus */

/**
 * @typedef {Object} CourseCard  serializer group course_list
 * @property {number} id
 * @property {string} title
 * @property {?string} description
 * @property {CourseStatus} status
 * @property {boolean} private
 * @property {?number} deadline_days
 * @property {Category} category
 * @property {number} module_count
 * @property {number} content_count
 */

/**
 * @typedef {Object} Option
 * @property {number} id
 * @property {string} text
 * @property {boolean} is_correct  back office only; never store where learner code can read it
 * @property {number} sequence
 */

/**
 * @typedef {Object} Question
 * @property {number} id
 * @property {'mcq_single'|'mcq_multi'} type
 * @property {string} text
 * @property {number} sequence
 * @property {Array<Option>} options
 */

/**
 * @typedef {Object} Quiz  serializer group course_content_quiz_details
 * @property {number} id
 * @property {string} title
 * @property {number} sequence
 * @property {number} passing_score_pct
 * @property {Array<Question>} questions
 */

/**
 * @typedef {Object} Content
 * @property {number} id
 * @property {'video_url'|'text'} type
 * @property {string} title
 * @property {?string} youtube_url
 * @property {?string} video_id  normalised 11-char YouTube id
 * @property {?string} text_body
 * @property {?number} min_view_pct
 * @property {number} sequence
 * @property {Array<Quiz>} quizzes
 */

/** @typedef {{ id: number, title: string, sequence: number, contents: Array<Content> }} Module */

/** @typedef {CourseCard & { modules: Array<Module> }} CourseDetails  serializer group course_details */

/**
 * @typedef {Object} CoursePayload
 * @property {string} title  2-200 chars, required on PATCH too
 * @property {?string} [description]
 * @property {number} category_id  required on PATCH too
 * @property {boolean} [private]
 * @property {?number} [deadline_days]  int >= 1 or null
 */

/**
 * @typedef {Object} ContentPayload
 * @property {number} [module_id]  create only
 * @property {'video_url'|'text'} [type]
 * @property {string} [title]
 * @property {string} [youtube_url]
 * @property {string} [text_body]
 * @property {number} [min_view_pct]  1-100, video only
 */

/** `id` only on question PATCH, for an option that is kept. @typedef {{ id?: number, text: string, is_correct: boolean }} OptionPayload */

/** @typedef {{ type: 'mcq_single'|'mcq_multi', text: string, options: Array<OptionPayload> }} QuestionPayload */

/**
 * @typedef {Object} QuizPayload
 * @property {number} [content_id]  create only
 * @property {string} [title]
 * @property {number} [passing_score_pct]
 * @property {Array<QuestionPayload>} [questions]  create only; required on a published course
 */

/**
 * @typedef {Object} AssignmentCriteria
 * @property {Array<number>} [company_ids]
 * @property {Array<number>} [office_ids]
 * @property {Array<number>} [tree_ids]  Group\Tree node ids, descendants included by the backend
 * @property {Array<number>} [designation_ids]
 * @property {Array<number>} [level_ids]
 * @property {Array<number>} [grade_ids]
 * @property {Array<number>} [employee_ids]
 */

/**
 * @typedef {Object} Assignment  serializer group course_assignment_details
 * @property {number} id
 * @property {CourseCard} course
 * @property {Array<number>} company_ids
 * @property {Array<number>} office_ids
 * @property {Array<number>} tree_ids
 * @property {Array<number>} designation_ids
 * @property {Array<number>} level_ids
 * @property {Array<number>} grade_ids
 * @property {Array<number>} employee_ids
 * @property {number} enrolled_count
 * @property {number} skipped_count
 * @property {string} created  'Y-m-d H:i:s' UTC
 * @property {{ id: number, name: string }} created_by
 */

/** @typedef {{ id: number, eid: string, name: string }} EnrolledEmployee */

/** @typedef {{ success: boolean, message: string, result: null }} Success */

const CATEGORIES = '/categories';
const MODULES = '/modules';
const CONTENTS = '/contents';
const QUIZZES = '/contents/quizzes';
const QUESTIONS = '/contents/quizzes/questions';
const OPTIONS = '/contents/quizzes/questions/options';
const ASSIGNMENTS = '/assignments';
const ENROLLED_EMPLOYEES = '/enrollments/employees';

export class Lms extends ServiceBase {
  async send(method, route, body, extra = {}) {
    return await request(this.API_ENDPOINT + route, { method, body, ...extra });
  }

  // Categories (flat, not paginated)
  /** @returns {Promise<Array<Category>>} */
  listCategories() { return this.send(FETCH_TYPES.GET, CATEGORIES); }
  /** @returns {Promise<Category>} */
  createCategory(body) { return this.send(FETCH_TYPES.POST, CATEGORIES, body); }
  /** @returns {Promise<Category>} */
  updateCategory(id, body) { return this.send(FETCH_TYPES.PATCH, `${CATEGORIES}/${id}`, body); }
  /** @returns {Promise<Success>} */
  deleteCategory(id) { return this.send(FETCH_TYPES.DELETE, `${CATEGORIES}/${id}`); }

  // Courses; find(query) is inherited: status, category_id, title (prefix), private, page, limit, sort
  /** @returns {Promise<CourseDetails>} */
  getCourse(id) { return this.send(FETCH_TYPES.GET, `/${id}`); }
  /** @param {CoursePayload} body @returns {Promise<CourseDetails>} */
  createCourse(body) { return this.send(FETCH_TYPES.POST, '', body); }
  /** @param {CoursePayload} body @returns {Promise<CourseDetails>} */
  updateCourse(id, body) { return this.send(FETCH_TYPES.PATCH, `/${id}`, body); }
  /** Drafts only. @returns {Promise<Success>} */
  deleteCourse(id) { return this.send(FETCH_TYPES.DELETE, `/${id}`); }
  /** @returns {Promise<CourseDetails>} */
  publishCourse(id) { return this.send(FETCH_TYPES.PATCH, `/${id}/publish`); }
  /** @returns {Promise<CourseDetails>} */
  unpublishCourse(id) { return this.send(FETCH_TYPES.PATCH, `/${id}/unpublish`); }

  // Modules (return the full course)
  /** @param {{course_id: number, title: string}} body @returns {Promise<CourseDetails>} */
  createModule(body) { return this.send(FETCH_TYPES.POST, MODULES, body); }
  /** @returns {Promise<CourseDetails>} */
  updateModule(id, body) { return this.send(FETCH_TYPES.PATCH, `${MODULES}/${id}`, body); }
  /** @returns {Promise<CourseDetails>} */
  deleteModule(id) { return this.send(FETCH_TYPES.DELETE, `${MODULES}/${id}`); }
  /** @param {{course_id: number, module_ids: Array<number>}} body every live id @returns {Promise<CourseDetails>} */
  reorderModules(body) { return this.send(FETCH_TYPES.PATCH, `${MODULES}/reorder`, body); }

  // Contents (return the full course)
  /** @param {ContentPayload} body @returns {Promise<CourseDetails>} */
  createContent(body) { return this.send(FETCH_TYPES.POST, CONTENTS, body); }
  /** @param {ContentPayload} body @returns {Promise<CourseDetails>} */
  updateContent(id, body) { return this.send(FETCH_TYPES.PATCH, `${CONTENTS}/${id}`, body); }
  /** @returns {Promise<CourseDetails>} */
  deleteContent(id) { return this.send(FETCH_TYPES.DELETE, `${CONTENTS}/${id}`); }
  /** @param {{module_id: number, content_ids: Array<number>}} body every live id @returns {Promise<CourseDetails>} */
  reorderContents(body) { return this.send(FETCH_TYPES.PATCH, `${CONTENTS}/reorder`, body); }

  // Quizzes, questions, options (return the quiz)
  /** @param {QuizPayload} body @returns {Promise<Quiz>} */
  createQuiz(body) { return this.send(FETCH_TYPES.POST, QUIZZES, body); }
  /** @param {QuizPayload} body @returns {Promise<Quiz>} */
  updateQuiz(id, body) { return this.send(FETCH_TYPES.PATCH, `${QUIZZES}/${id}`, body); }
  /** @returns {Promise<Success>} */
  deleteQuiz(id) { return this.send(FETCH_TYPES.DELETE, `${QUIZZES}/${id}`); }
  /** Answers the content's quizzes in their new order. @returns {Promise<Array<Quiz>>} */
  reorderQuizzes(body) { return this.send(FETCH_TYPES.PATCH, `${QUIZZES}/reorder`, body); }
  /** @param {QuestionPayload & {quiz_id: number}} body @returns {Promise<Quiz>} */
  createQuestion(body) { return this.send(FETCH_TYPES.POST, QUESTIONS, body); }
  /**
   * `options` is the full list: kept options with their id, new ones without, missing ones are deleted;
   * an answered option whose text or is_correct changes comes back with a new id. Omit or null keeps type/text.
   * The only way to change options of a published or unpublished course. @returns {Promise<Quiz>}
   */
  updateQuestion(id, body) { return this.send(FETCH_TYPES.PATCH, `${QUESTIONS}/${id}`, body); }
  /** @returns {Promise<Quiz>} */
  deleteQuestion(id) { return this.send(FETCH_TYPES.DELETE, `${QUESTIONS}/${id}`); }
  /** Any status; validated like any live edit on a published course. @param {OptionPayload & {question_id: number}} body @returns {Promise<Quiz>} */
  createOption(body) { return this.send(FETCH_TYPES.POST, OPTIONS, body); }
  /** Drafts only (400 option_id on a live course). @returns {Promise<Quiz>} */
  updateOption(id, body) { return this.send(FETCH_TYPES.PATCH, `${OPTIONS}/${id}`, body); }
  /** Drafts only (400 option_id on a live course). @returns {Promise<Quiz>} */
  deleteOption(id) { return this.send(FETCH_TYPES.DELETE, `${OPTIONS}/${id}`); }

  // Assignments (immutable) and the enrolled-employees lookup
  /** @returns {Promise<Assignment>} */
  getAssignment(id) { return this.send(FETCH_TYPES.GET, `${ASSIGNMENTS}/${id}`); }
  /**
   * 409 (concurrent enrolment race, nothing saved, retry is safe) is re-thrown to the caller.
   * @param {AssignmentCriteria & {course_id: number}} body @returns {Promise<Assignment>}
   */
  createAssignment(body) { return this.send(FETCH_TYPES.POST, ASSIGNMENTS, body, { rethrow: [409] }); }
  /** Not paginated, ordered by name. @returns {Promise<Array<EnrolledEmployee>>} */
  enrolledEmployees(courseId) {
    return this.send(FETCH_TYPES.GET, ENROLLED_EMPLOYEES + encodeQuery({ course_id: courseId }));
  }

  // Certificates backoffice
  /** @returns {Promise<Success>} */
  revokeCertificate(certificateId) {
    return this.send(FETCH_TYPES.POST, `/certificates/backoffice/${certificateId}/revoke`);
  }
}
